import { useMemo, useState } from 'react'

import { ActionCard } from '@playbooks/core/src/interfaces'
import { ViewMode } from '@playbooks/action-cards-utils'
import useMountEffect from './useMountEffect'

/**
 * Custom hook to determine if the pinned section is enabled based on the container width, action cards data and view mode.
 */
export default function usePinnedSectionEnabled(
  containerRef: React.RefObject<HTMLDivElement | null>,
  actionCardsData: ActionCard,
  viewMode: ViewMode,
) {
  const isFunnelView = viewMode === ViewMode.FUNNEL
  const [pinnedSectionEnabled, setPinnedSectionEnabled] = useState(false)

  const hasPinnedSection = useMemo(
    () => actionCardsData.sections.some(section => section.isPinned),
    [actionCardsData.sections],
  )

  useMountEffect(() => {
    if (!hasPinnedSection || isFunnelView) return

    const MIN_CONTAINER_WIDTH_FOR_PINNED_SECTION = 700

    // Observe the container's width and enable the pinned section
    // if it exceeds the minimum container width for pinned section
    const observer = new ResizeObserver(() => {
      if (isFunnelView) return
      setPinnedSectionEnabled((containerRef.current?.clientWidth ?? 0) > MIN_CONTAINER_WIDTH_FOR_PINNED_SECTION)
    })

    observer.observe(containerRef.current ?? document.body)
    return () => {
      observer.disconnect()
    }
  })

  return hasPinnedSection && pinnedSectionEnabled
}
